import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Inject,
  NgZone,
  viewChild,
} from '@angular/core';
import { IconComponent } from '@examdojo/core/icon';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { MatMenuModule } from '@angular/material/menu';
import type { IconPickerSelection } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker';
import { NodeSelection } from '@tiptap/pm/state';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';
import { DEFAULT_ACCORDION_PANEL_TITLE } from './accordion.model';
import { IconPickerWrapperComponent } from './icon-picker-wrapper.component';
import {
  getIconBgColor,
  parseAccordionIcon,
  serializeAccordionIcon,
  type ParsedAccordionIcon,
} from './accordion-icon.util';

interface AccordionPanelAttrs {
  title: string;
  icon: string;
  expanded: boolean;
}

@Component({
  selector: 'dojo-accordion-panel',
  standalone: true,
  imports: [
    TiptapNodeViewContentDirective,
    StopPropagationDirective,
    IconComponent,
    MatMenuModule,
    IconPickerWrapperComponent,
  ],
  templateUrl: './accordion-panel.component.html',
  styleUrls: ['./accordion-panel.component.scss'],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => AccordionPanelComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccordionPanelComponent extends NodeViewComponent<AccordionPanelAttrs> {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    super();

    effect(() => {
      const element = this.titleElement()?.nativeElement;
      if (element && this.document.activeElement !== element) {
        element.textContent = this.title();
      }
    });
  }

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly titleElement = viewChild<ElementRef<HTMLSpanElement>>('titleElement');

  readonly title = this.getProp('title');
  readonly iconRaw = this.getProp('icon');
  readonly parsedIcon = computed<ParsedAccordionIcon | null>(() => parseAccordionIcon(this.iconRaw()));
  readonly iconBgColor = computed(() => {
    const pi = this.parsedIcon();
    return pi?.showBackground ? getIconBgColor(pi.color) : null;
  });
  readonly isEditable = computed(() => this.editor().isEditable);
  readonly expanded = this.getProp('expanded');

  readonly initialPickerTab = computed(() => {
    const pi = this.parsedIcon();
    if (!pi) {
      return 'fontawesome' as const;
    }
    switch (pi.type) {
      case 'fa':
        return 'fontawesome' as const;
      case 'emoji':
        return 'emojis' as const;
      case 'img':
        return 'icons' as const;
    }
  });
  readonly initialPickerFaColor = computed(() => {
    const pi = this.parsedIcon();
    return pi?.type === 'fa' ? pi.color : undefined;
  });
  readonly initialPickerShowBg = computed(() => {
    const pi = this.parsedIcon();
    return pi?.type === 'fa' ? pi.showBackground ?? true : true;
  });
  readonly activeIconValue = computed(() => {
    const pi = this.parsedIcon();
    return pi?.value;
  });

  override getDefaults = (): AccordionPanelAttrs => ({
    title: DEFAULT_ACCORDION_PANEL_TITLE,
    icon: '',
    expanded: false,
  });

  selectAccordion(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const resolvedPos = editor.state.doc.resolve(pos);
    const accordionPos = resolvedPos.before(resolvedPos.depth);
    const { tr } = editor.state;
    tr.setSelection(NodeSelection.create(tr.doc, accordionPos));
    editor.view.dispatch(tr);
    editor.view.focus();
  }

  toggleExpanded(): void {
    this.ngZone.run(() => {
      const willExpand = !this.expanded();
      this.updateProp('expanded', willExpand);
      this.cdr.detectChanges();

      if (willExpand && this.isEditable()) {
        const pos = this.getPos()();
        const node = this.node();
        const endOfPanel = pos + node.nodeSize - 2;
        this.editor().commands.focus(endOfPanel);
      }
    });
  }

  selectIcon(selection: IconPickerSelection): void {
    this.updateProp('icon', serializeAccordionIcon(selection));
  }

  removeIcon(): void {
    this.updateProp('icon', '');
  }

  updateTitle(event: Event): void {
    const target = event.target as HTMLElement;
    const newTitle = target.textContent || '';
    this.updateProp('title', newTitle);
  }

  movePanelUp(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();
    const resolvedPos = editor.state.doc.resolve(pos);
    const index = resolvedPos.index();
    const parent = resolvedPos.parent;

    if (index === 0) {
      // Wrap: move first panel to the end
      const parentEnd = resolvedPos.start() + parent.content.size;
      const { tr } = editor.state;
      const nodeSlice = tr.doc.slice(pos, pos + node.nodeSize);
      tr.delete(pos, pos + node.nodeSize);
      tr.insert(parentEnd - node.nodeSize, nodeSlice.content);
      editor.view.dispatch(tr);
      return;
    }

    // Find the previous sibling's position
    let prevPos = resolvedPos.start();
    for (let i = 0; i < index - 1; i++) {
      prevPos += parent.child(i).nodeSize;
    }

    const { tr } = editor.state;
    const nodeSlice = tr.doc.slice(pos, pos + node.nodeSize);
    tr.delete(pos, pos + node.nodeSize);
    tr.insert(prevPos, nodeSlice.content);
    editor.view.dispatch(tr);
  }

  movePanelDown(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();
    const resolvedPos = editor.state.doc.resolve(pos);
    const index = resolvedPos.index();
    const parent = resolvedPos.parent;

    if (index >= parent.childCount - 1) {
      // Wrap: move last panel to the beginning
      const parentStart = resolvedPos.start();
      const { tr } = editor.state;
      const nodeSlice = tr.doc.slice(pos, pos + node.nodeSize);
      tr.delete(pos, pos + node.nodeSize);
      tr.insert(parentStart, nodeSlice.content);
      editor.view.dispatch(tr);
      return;
    }

    const nextNode = parent.child(index + 1);
    const nextPos = pos + node.nodeSize;

    const { tr } = editor.state;
    const nextSlice = tr.doc.slice(nextPos, nextPos + nextNode.nodeSize);
    tr.delete(nextPos, nextPos + nextNode.nodeSize);
    tr.insert(pos, nextSlice.content);
    editor.view.dispatch(tr);
  }

  removePanel(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();

    // Find parent accordion and check panel count
    const resolvedPos = editor.state.doc.resolve(pos);
    const parent = resolvedPos.parent;

    // Blur before dispatching to prevent "removeChild" errors
    // when ProseMirror detaches DOM that still has focused elements
    (this.document.activeElement as HTMLElement)?.blur?.();

    const { tr } = editor.state;
    if (parent.childCount <= 1) {
      // Last panel: delete the entire accordion
      const parentPos = resolvedPos.before(resolvedPos.depth);
      tr.delete(parentPos, parentPos + parent.nodeSize);
    } else {
      tr.delete(pos, pos + node.nodeSize);
    }
    editor.view.dispatch(tr);
  }
}
